import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OTPinput from "Components/OTPinput/OTPinput";
import api from "http";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import { useDispatch } from "react-redux";
import { signin, signout } from "Redux/UserAuthSlice/UserAuthSlice";
import { addPatient, swtichTab } from "Redux/TabsSlice/TabsSlice";
import AccountBlocked from "Pages/LoginPage/AccountBlocked";
import { LuKeyRound } from "react-icons/lu";
import { toast } from "react-toastify";
import FullPageLoader from "Components/FullPageLoader/FullPageLoader";
import { Card } from "reactstrap";
import Spinner from "Common/Spinner";

const OtpVerificationPage = () => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isAccBlocked, setIsAccBlocked] = useState(false);
  const [loader, setloader] = useState(false);
  const [encryptedKey, setEncryptedKey] = useState("");
  const [resetOtp, setResetOtp] = useState(false); // State to reset OTP input

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const state = React.useMemo(() => location.state || {}, [location.state]);
  const searchParams = new URLSearchParams(location.search);
  const patientId = searchParams.get("patientId");
  const visitId = searchParams.get("visitId");

  const defaultLogin = () => {
    navigate("/");
    window.history.pushState(null, null, window.location.href);
  };
  const handlePopState = (event) => {
    window.history.pushState(null, null, window.location.href);
  };

  const navigateToLogin = () => {
    setIsAccBlocked(false);
    setError("");
    navigate("/login");
  };

  const handleGetOTP = (otp) => {
    setOtp(otp);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (otp === "") {
      setError("Please enter all digits");
      return;
    }
    try {
      setloader(true);
      const config = {
        headers: {
          Authorization: `Bearer ${state.token}`, // Add any custom headers here
        },
      };
      const payload = {
        otp: otp,
        encryptedKey: encryptedKey || "",
        transferVia: "",
        isTwoFactorEnable: true,
      };
      let endPoint = null;
      if (state.transferVia === "authenticator") {
        endPoint = "VerifyGoogleAuthenticatorCode";
      } else {
        endPoint = "VerifyOTP";
      }
      const respLogin = await api.post(
        `${process.env.REACT_APP_TC_AUTH}/api/TwoFactorAuthentication/${endPoint}`,
        payload,
        config
      );
      dispatch(
        signin({
          token: respLogin.data.dataModel.token,
          twoFactorRequired: respLogin.data.dataModel.twoFactorRequired,
          userPassword: state.encryptPassword,
          twoFactorViaAuthenticator:
            respLogin.data.dataModel.twoFactorViaAuthenticator,
          twoFactorViaEmail: respLogin.data.dataModel.twoFactorViaEmail,
          twoFactorViaText: respLogin.data.dataModel.twoFactorViaText,
        })
      );
      const restrictedAccessEmails = [
        "cneal@southlandneuromuscular.com",
        "citlalytgarcia86@gmail.com",
      ];
      if (restrictedAccessEmails.includes(state.email.toLowerCase())) {
        const patientObj = {
          active: true,
          id: 0,
          accountNum: 0,
          subTab: "",
          name: "Search",
          unsavedChanges: false,
          insuranceData: { status: false, editMode: false },
        };
        dispatch(addPatient(patientObj));
      }
      if (visitId) {
        try {
          // const visitResp = await api.get(`${process.env.REACT_APP_TC_PMS}/api/Visit/FindVisit/${visitId}`,)
          const visitobj = {
            visitId: visitId,
            patientId: null,
          };
          dispatch(
            swtichTab({
              parentName: "charges",
              subTabName: "claim",
              subTabData: visitobj,
            })
          );
        } catch (error) {
          defaultLogin();
        }
      } else if (patientId) {
        try {
          const ptHeaderResp = await api.get(
            `${process.env.REACT_APP_TC_PMS}/api/Patient/FindPatientHeaderInfoById/${patientId}`
          );
          const patientObj = {
            active: true,
            id: ptHeaderResp.data.dataModel.id,
            accountNum: ptHeaderResp.data.dataModel.accountNum,
            subTab: "Overview",
            name: ptHeaderResp.data.dataModel.name,
            unsavedChanges: false,
            insuranceData: { status: false, editMode: false },
          };
          dispatch(addPatient(patientObj));
        } catch (error) {
          defaultLogin();
        }
      }
      // Prevent going back to the login page after successful login
      navigate("/");
      window.history.pushState(null, null, window.location.href);
      window.addEventListener("popstate", handlePopState);
    } catch (error) {
      setError(error.response.data.responseMessage);
      if (
        error.response.data.responseMessage.includes(
          "Account Blocked Due to Excessive Login Attempts"
        )
      ) {
        setIsAccBlocked(true);
      }
      setResetOtp((prev) => !prev);
      dispatch(signout());
      ErrorHandlingMessage(error);
    } finally {
      setloader(false);
    }
  };

  useEffect(() => {
    if (state.transferVia !== "authenticator") {
      (async () => {
        try {
          setloader(true);
          const config = {
            headers: {
              Authorization: `Bearer ${state.token}`, // Add any custom headers here
            },
          };

          const resp = await api.get(
            `${process.env.REACT_APP_TC_AUTH}/api/TwoFactorAuthentication/SendOTP?TransferVia=${state.transferVia}`,
            config
          );
          toast.success(resp.data.responseMessage);
          setEncryptedKey(resp.data.dataModel[0]);
        } catch (error) {
          ErrorHandlingMessage(error);
        } finally {
          setloader(false);
        }
      })();
    }
  }, [state]);

  return (
    <div className="flex items-center justify-center min-h-screen bg_gradient">
      {/* {loader && <FullPageLoader allowFullScreen />} */}
      {isAccBlocked ? (
        <AccountBlocked navigateToLogin={navigateToLogin} email={state.email} />
      ) : (
        <div>
          <Card className="w-full max-w-md">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100">
                <LuKeyRound size={33} />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">
                Two-Factor Authentication
              </h2>
              <p className="mt-4 font-semibold text-md ">
                {state.transferVia === "authenticator"
                  ? "Please enter the 6-digit verification code from your Google Authenticator app below."
                  : state.transferVia === "email"
                    ? "We've sent a verification code to your email address. Please enter it below."
                    : "We've sent a verification code to your phone number. Please enter it below."}
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <OTPinput
                onComplete={handleGetOTP}
                reset={resetOtp}
                className="bg-transparent border-zinc-500"
              />
              {error && (
                <p className="mt-3 mb-4 text-sm text-center text-red-600">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loader}
                className="flex items-center justify-center w-full h-16 mr-1 text-white transition-all rounded-md bg-primary"
              >
                {loader && <Spinner size={20} />} Verify Code
              </button>
              <span
                onClick={navigateToLogin}
                className="block mr-4 mt-2 text-sm text-right text-gray-500 cursor-pointer hover:underline"
              >
                Back to Login
              </span>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OtpVerificationPage;
